import { Configuration, ExpirationData, Rollout } from './interfaces';
import * as cache from './cache';
import { fetchRolloutData, throttledFetchMultiRolloutData } from './api';
import { count, log, LOG_LEVELS } from './metrics';
import { NotFoundError } from './errors';
import { config } from './configuration';
import { ExecutionLimitError } from './api/utils';

/**
 * Fetches all rollouts defined in configuration and stores them in the cache.
 *
 * @param configuration {Configuration} package config
 */
export const initialPopulateCache = async (configuration: Configuration): Promise<void> => {
    if (!configuration.rollouts?.length) {
        return;
    }
    try {
        const rollouts = await throttledFetchMultiRolloutData(configuration.rollouts);
        cache.setMulti(rollouts);
    } catch (error) {
        count('errors.init_fetch');
        log(LOG_LEVELS.ERROR, error as Error, { message: 'Failed to fetch rollouts in initialization' });
    }
};

/**
 * Checks `cache` for rollout data (most common case so long as rollouts are initiated). Refreshes ALL rollouts in cache
 * if requested rollout is expired.
 * Checks `notFoundCache` to see if rollout was not found on previous fetch (to avoid fetching again).
 * Fetches rollout data from service if not in cache.
 *
 * @param {string} name - rollout name
 */
export const getRolloutData = async (name: string): Promise<Rollout> => {
    const cachedRollout = cache.get(name);
    if (cachedRollout) {
        if (expired(cachedRollout)) {
            refreshCache(); // not awaited
        }

        return cachedRollout;
    }

    const notFound = cache.getNotFound(name);
    if (notFound) {
        if (expired(notFound)) {
            cache.deleteNotFound(name); // delete so another fetch is attempted on next call
        }

        throw new NotFoundError(name);
    }

    // attempt fetch if not in cache (not initiated or not found)
    try {
        const rollout = await fetchRolloutData(name, config.fetchTimeout);
        cache.set(name, rollout);
        logPerformanceWarning(name);

        return rollout;
    } catch (error) {
        count(`errors.fetch.${name}`);
        if (error instanceof ExecutionLimitError) {
            secondFetchOnExecutionLimit(name);
        }

        if ((error as Record<string, any>)?.response?.status === 404) {
            // 404 means rollout doesn't exist
            count(`errors.fetch_not_found.${name}`);
            cache.setNotFound(name);

            throw new NotFoundError(name);
        }

        throw error;
    }
};

/**
 * Determines if a given rollout is expired or not.
 *
 * @param {ExpirationData} data The cache key to check
 * @returns {boolean}
 */
const expired = (data: ExpirationData): boolean => Date.now() - data.date > config.cacheTtl;

/**
 * Refreshes all rollout data in the cache.
 */
const refreshCache = async (): Promise<void> => {
    const names = cache.keys();
    try {
        const rollouts = await throttledFetchMultiRolloutData(names);
        cache.setMulti(rollouts);
    } catch (err) {
        log(LOG_LEVELS.WARN, err as Error, { extra: `Refresh failed for rollouts: ${names}` });
        count(`errors.refresh_fail.${names.length}`);
    }
};

/**
 * Attempts to fetch rollout data again after an execution limit error - no timeout.
 *
 * @param {string} name - rollout name to fetch
 */
const secondFetchOnExecutionLimit = async (name: string): Promise<void> => {
    try {
        const rollout = await fetchRolloutData(name, 0);
        cache.set(name, rollout);
    } catch (err) {
        log(LOG_LEVELS.ERROR, err as Error, {
            extra: `Second fetch (after first timed out) failed for rollout: ${name}`,
        });
        count(`errors.second_fetch_fail.${name}`);
    }
};

const logPerformanceWarning = (name: string) => {
    log(LOG_LEVELS.WARN, `Rollout: "${name}" was not included in initialization. Performance will be impacted.`);
};
